import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  PanelLeft,
  Plus,
  Folder,
  Home,
  Search,
  Star,
  Clock,
  Settings,
  LogOut,
  Palette,
  BookOpen,
  Keyboard,
  MoreVertical,
  Share2,
  Pencil,
  Pin,
  Trash2,
  Globe,
  Loader2,
  ArrowUpCircle,
  ArrowRight,
  Check,
  Users,
  ChevronDown,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "next-themes";
import { formatDistanceToNow } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShareDialog } from "./ShareDialog";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { usePrefetchAnalysis } from "@/hooks/useAnalysisData";
import { useTranslation } from "react-i18next";
import { languages } from "@/i18n/config";

// Lazy load heavy modal


interface RecentAnalysis {
  id: string;
  title: string;
  updated_at: string;
  is_pinned: boolean;
  is_shared: boolean | null;
  share_token: string | null;
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { tier, isAdmin, subscription } = useSubscription();
  const { t, i18n } = useTranslation();
  
  const [user, setUser] = useState<{ email: string; id: string } | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [recentAnalyses, setRecentAnalyses] = useState<RecentAnalysis[]>([]);
  
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareAnalysis, setShareAnalysis] = useState<RecentAnalysis | null>(null);
  const [unreadInviteCount, setUnreadInviteCount] = useState(0);
  const queryClient = useQueryClient();
  const prefetchAnalysis = usePrefetchAnalysis();

  const handleUpgrade = () => {
    navigate('/upgrade');
  };

  const getPlanLabel = () => {
    if (isAdmin) return 'Admin';
    if (tier === 'pro') return 'Pro plan';
    if (tier === 'core') return 'Core plan';
    return 'Free plan';
  };

  // Fetch user and profile
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser({ email: user.email || "", id: user.id });
        
        // Fetch profile
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name")
          .eq("user_id", user.id)
          .single();
        
        if (profile?.display_name) {
          setDisplayName(profile.display_name);
        }
      }
    };

    fetchUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({ email: session.user.email || "", id: session.user.id });
      } else {
        setUser(null);
        setDisplayName(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch recent analyses with React Query for cache invalidation
  const { data: recentAnalysesData } = useQuery({
    queryKey: ['recentAnalyses', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data } = await supabase
        .from("analyses")
        .select("id, title, updated_at, is_pinned, is_shared, share_token")
        .eq("user_id", user.id)
        .order("is_pinned", { ascending: false })
        .order("updated_at", { ascending: false })
        .limit(10);

      return data || [];
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Nav counts (All analyses, Favourites)
  const { data: navCounts } = useQuery({
    queryKey: ['sidebarNavCounts', user?.id],
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const [analysesRes, favoritesRes] = await Promise.all([
        supabase.from("analyses").select("*", { count: "exact", head: true }).eq("user_id", user!.id),
        supabase.from("favorites").select("*", { count: "exact", head: true }).eq("user_id", user!.id),
      ]);
      return {
        analyses: analysesRes.count ?? 0,
        favorites: favoritesRes.count ?? 0,
      };
    },
  });

  // Update local state when query data changes
  useEffect(() => {
    if (recentAnalysesData) {
      setRecentAnalyses(recentAnalysesData);
    } else if (!user) {
      setRecentAnalyses([]);
    }
  }, [recentAnalysesData, user]);

  // Fetch unread invite count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) {
        setUnreadInviteCount(0);
        return;
      }
      
      if (!user?.email) return;

      const { count } = await supabase
        .from("analysis_invites")
        .select("*", { count: "exact", head: true })
        .eq("email", user.email.toLowerCase())
        .neq("invited_by", user.id)
        .is("accepted_at", null);

      setUnreadInviteCount(count || 0);
    };

    fetchUnreadCount();
  }, [user]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const getUserInitial = () => {
    if (displayName) return displayName.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return "U";
  };

  const getDisplayNameOrEmail = () => {
    if (displayName) return displayName;
    if (user?.email) return user.email.split("@")[0];
    return "User";
  };

  // Strip author from title for compact display (e.g., "The Great Gatsby by Fitzgerald" -> "The Great Gatsby")
  const getBookTitleOnly = (title: string) => {
    const byIndex = title.toLowerCase().lastIndexOf(" by ");
    if (byIndex > 0) {
      return title.substring(0, byIndex);
    }
    return title;
  };

  // Recents action handlers
  const handleShare = (analysis: RecentAnalysis) => {
    setShareAnalysis(analysis);
    setShareDialogOpen(true);
  };

  const handleShareChange = (isShared: boolean, shareToken: string | null) => {
    // Update local state and invalidate query to refresh data
    if (shareAnalysis) {
      setShareAnalysis({ ...shareAnalysis, is_shared: isShared, share_token: shareToken });
    }
    queryClient.invalidateQueries({ queryKey: ['recentAnalyses'] });
  };

  const handleRename = async (analysisId: string, newTitle: string) => {
    if (!newTitle.trim()) {
      setRenamingId(null);
      return;
    }
    
    const { error } = await supabase
      .from("analyses")
      .update({ title: newTitle.trim() })
      .eq("id", analysisId);
    
    if (error) {
      toast.error(t('toast.renameFailed'));
    } else {
      toast.success(t('toast.renamed'));
      queryClient.invalidateQueries({ queryKey: ['recentAnalyses'] });
    }
    setRenamingId(null);
  };

  const handlePin = async (analysisId: string, currentlyPinned: boolean) => {
    const { error } = await supabase
      .from("analyses")
      .update({ is_pinned: !currentlyPinned })
      .eq("id", analysisId);
    
    if (error) {
      toast.error(t('toast.pinFailed'));
    } else {
      toast.success(currentlyPinned ? t('toast.unpinned') : t('toast.pinned'));
      queryClient.invalidateQueries({ queryKey: ['recentAnalyses'] });
    }
  };

  const handleDelete = async (analysisId: string) => {
    const { error } = await supabase
      .from("analyses")
      .delete()
      .eq("id", analysisId);
    
    if (error) {
      toast.error(t('toast.deleteFailed'));
    } else {
      toast.success(t('toast.deleted'));
      queryClient.invalidateQueries({ queryKey: ['recentAnalyses'] });
    }
  };

  // Get current analysis ID from path
  const currentAnalysisId = location.pathname.startsWith("/analysis/") 
    ? location.pathname.split("/")[2] 
    : null;

  // Keep all recents visible including the current analysis

  const navItems = [
    {
      label: t('nav.home', { defaultValue: 'Home' }),
      icon: Home,
      path: "/",
    },
    {
      label: t('nav.allAnalyses'),
      icon: Folder,
      path: "/analyses",
      badgeCount: navCounts?.analyses ?? 0,
    },
    {
      label: t('nav.favorites'),
      icon: Star,
      path: "/favorites",
      badgeCount: navCounts?.favorites ?? 0,
    },
    {
      label: "Shared with Me",
      icon: Users,
      path: "/shared-with-me",
      badgeCount: unreadInviteCount,
    },
  ];

  return (
    <>
      <Sidebar
        collapsible="icon"
        className="border-r border-[hsl(var(--ink)/0.08)]"
        style={{
          // Warm-editorial chrome scoped to this Sidebar only.
          // Global --sidebar-* tokens remain unchanged.
          ["--sidebar-background" as any]: "var(--surface-cream)",
          ["--sidebar-foreground" as any]: "var(--ink)",
          ["--sidebar-accent" as any]: "40 18% 91%",
          ["--sidebar-accent-foreground" as any]: "var(--ink)",
          ["--sidebar-border" as any]: "24 14% 9%",
          ["--sidebar-ring" as any]: "var(--amber-accent)",
          ["--sidebar-primary" as any]: "var(--ink)",
          ["--sidebar-primary-foreground" as any]: "var(--surface-cream)",
        } as React.CSSProperties}
      >
        <SidebarHeader className="p-3">
          <div className="flex items-center justify-center">
            {isCollapsed ? (
              // Collapsed: Smart logo trigger with floating tooltip
              <div className="relative">
                <button
                  onMouseEnter={() => setIsLogoHovered(true)}
                  onMouseLeave={() => setIsLogoHovered(false)}
                  onClick={toggleSidebar}
                  className="flex items-center justify-center w-10 h-10 rounded-lg bg-[hsl(var(--ink)/0.06)] hover:bg-[hsl(var(--ink)/0.10)] transition-colors cursor-pointer"
                >
                  <BookOpen 
                    className={cn(
                      "h-5 w-5 text-[hsl(var(--ink))] absolute transition-opacity duration-150",
                      isLogoHovered ? "opacity-0" : "opacity-100"
                    )} 
                  />
                  <PanelLeft 
                    className={cn(
                      "h-5 w-5 text-[hsl(var(--ink))] absolute transition-opacity duration-150",
                      isLogoHovered ? "opacity-100" : "opacity-0"
                    )} 
                  />
                </button>

                {isLogoHovered && (
                  <div className="absolute left-full ml-3 top-0 translate-y-1.5
                                  bg-popover border border-border rounded-md px-3 py-1.5 shadow-md
                                  text-sm text-foreground whitespace-nowrap z-50
                                  animate-fade-in">
                    {t('nav.openSidebar')}
                  </div>
                )}
              </div>
            ) : (
              // Expanded: logo, wordmark, and collapse button
              <div className="flex items-center gap-2 w-full">
                <Link
                  to="/"
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className="flex items-center justify-center w-7 h-7 rounded-md bg-[hsl(var(--ink))]">
                    <span className="font-serif text-[15px] font-bold text-[hsl(var(--surface-cream))] leading-none">A</span>
                  </div>
                  <span className="font-serif text-[16px] font-bold tracking-[-0.02em] text-[hsl(var(--ink))]">
                    AnnotateAI
                  </span>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  className="h-8 w-8 ml-auto cursor-pointer text-[hsl(var(--muted-warm))] hover:bg-[hsl(var(--ink)/0.06)] hover:text-[hsl(var(--ink))] transition-colors"
                >
                  <PanelLeft className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </SidebarHeader>


        <SidebarContent>
          {/* Personal search + New analysis pill (v7) */}
          {!isCollapsed && (
            <div className="px-3 pt-1 pb-2 space-y-2">
              <div className="flex items-center gap-2 h-8 px-3 rounded-md bg-white border border-[hsl(var(--ink)/0.08)] text-[hsl(var(--muted-warm))]">
                <Search className="h-3.5 w-3.5 shrink-0" />
                <input
                  type="text"
                  placeholder={t('nav.searchYourWork', { defaultValue: 'Search your work…' })}
                  className="flex-1 bg-transparent outline-none text-[12.5px] placeholder:text-[hsl(var(--muted-warm))] min-w-0"
                />
                <span className="text-[10.5px] text-[hsl(var(--muted-warm))] tracking-wide">⌘K</span>
              </div>
              <button
                onClick={() => navigate('/')}
                className="w-full flex items-center justify-center gap-2 h-10 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--surface-cream))] text-[13px] font-semibold tracking-[-0.01em] hover:bg-[hsl(var(--ink)/0.9)] transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t('nav.newAnalysis')}
              </button>
            </div>
          )}
          {/* Main Navigation */}
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === item.path}
                      className="rounded-md text-[hsl(var(--muted-warm))] hover:text-[hsl(var(--ink))] hover:bg-[hsl(var(--ink)/0.06)] data-[active=true]:bg-[hsl(var(--ink)/0.08)] data-[active=true]:text-[hsl(var(--ink))] data-[active=true]:font-semibold"
                      tooltip={item.label}
                    >
                      <Link to={item.path} className="flex items-center gap-2 w-full">
                        <item.icon className="h-4 w-4" />
                        <span className="flex-1">{item.label}</span>
                        {!!item.badgeCount && item.badgeCount > 0 && (
                          <span className="ml-auto text-[11px] leading-none px-1.5 py-0.5 rounded-full bg-[hsl(var(--ink)/0.08)] text-[hsl(var(--muted-warm))] font-medium">
                            {item.badgeCount}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Recents Section - Hidden when collapsed */}
          {!isCollapsed && user && recentAnalyses.length > 0 && (
            <>
              <Separator className="mx-3 my-2" />
              <SidebarGroup>
                <SidebarGroupLabel>{t('nav.recents')}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {recentAnalyses.slice(0, 8).map((analysis, idx) => {
                      const dotColors = ['hsl(var(--spine-gold))', 'hsl(var(--spine-teal))', 'hsl(var(--spine-mauve))'];
                      const dot = dotColors[idx % 3];
                      return (
                      <SidebarMenuItem key={analysis.id} className="group/recent">
                        <SidebarMenuButton
                          asChild
                          isActive={location.pathname === `/analysis/${analysis.id}`}
                          className="h-auto py-1.5 pr-8"
                        >
                          <Link to={`/analysis/${analysis.id}`} onMouseEnter={() => prefetchAnalysis(analysis.id)}>
                            {analysis.is_pinned ? (
                              <Pin className="h-3 w-3 shrink-0 text-primary" />
                            ) : (
                              <span
                                className="h-2 w-2 shrink-0 rounded-full"
                                style={{ background: dot }}
                                aria-hidden
                              />
                            )}
                            <div className="flex flex-col min-w-0">
                              {renamingId === analysis.id ? (
                                <input
                                  type="text"
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onBlur={() => handleRename(analysis.id, renameValue)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleRename(analysis.id, renameValue);
                                    if (e.key === 'Escape') setRenamingId(null);
                                  }}
                                  onClick={(e) => e.preventDefault()}
                                  autoFocus
                                  className="text-sm bg-transparent border-b border-primary outline-none w-full"
                                />
                              ) : (
                                <span className="truncate text-[13px] text-[hsl(var(--ink))]">{getBookTitleOnly(analysis.title)}</span>
                              )}
                            </div>
                          </Link>
                        </SidebarMenuButton>
                        
                        {/* More options dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover/recent:opacity-100 hover:bg-muted transition-opacity"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" side="right" className="w-40">
                            <DropdownMenuItem 
                              onClick={() => handleShare(analysis)}
                              className="cursor-pointer"
                            >
                              <Share2 className="mr-2 h-4 w-4" />
                              {t('nav.share')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setRenamingId(analysis.id);
                                setRenameValue(analysis.title);
                              }}
                              className="cursor-pointer"
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('nav.rename')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handlePin(analysis.id, analysis.is_pinned)}
                              className="cursor-pointer"
                            >
                              <Pin className="mr-2 h-4 w-4" />
                              {analysis.is_pinned ? t('nav.unpin') : t('nav.pin')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setPendingDeleteId(analysis.id);
                                setDeleteDialogOpen(true);
                              }}
                              className="cursor-pointer text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('nav.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </>
          )}
        </SidebarContent>

        {/* User Account Footer */}
        {user && (
          <SidebarFooter className="p-2">
            {(() => {
              const trialEnd = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
              const daysLeft = trialEnd
                ? Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / 86400000))
                : null;
              const trialActive = daysLeft !== null && daysLeft > 0;
              const showUpgrade = !isCollapsed && !isAdmin && tier !== 'pro';
              const trialState = daysLeft === null ? null : daysLeft <= 2 ? 'urgent' : daysLeft <= 5 ? 'warning' : 'comfortable';
              const trialColor =
                trialState === 'urgent' ? '#A8341A' :
                trialState === 'warning' ? '#7A6000' :
                '#857A6E';
              return (
                <div className={cn(
                  !isCollapsed && "rounded-lg border border-[hsl(var(--ink)/0.10)] bg-white overflow-hidden"
                )}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className={cn(
                          "w-full justify-start gap-2.5 h-auto py-2 px-2.5 rounded-none hover:bg-[hsl(var(--ink)/0.04)]",
                          isCollapsed && "justify-center px-2 rounded-md"
                        )}
                      >
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarFallback className="bg-[hsl(var(--terracotta))] text-white text-sm font-medium">
                            {getUserInitial()}
                          </AvatarFallback>
                        </Avatar>
                        {!isCollapsed && (
                          <div className="flex flex-col items-start min-w-0 flex-1">
                            <span className="text-[13px] font-semibold text-[hsl(var(--ink))] truncate leading-tight">
                              {getDisplayNameOrEmail()}
                            </span>
                            <span
                              className="text-[10.5px] leading-tight mt-0.5 flex items-center gap-1"
                              style={{ color: trialActive ? trialColor : undefined }}
                            >
                              {trialActive ? (
                                <>
                                  {trialState !== 'comfortable' && (
                                    <span className="text-[7px] leading-none" aria-hidden>●</span>
                                  )}
                                  <span className="font-medium">{daysLeft} day{daysLeft === 1 ? '' : 's'} of trial left</span>
                                </>
                              ) : (
                                <span className="text-muted-foreground">{getPlanLabel()}</span>
                              )}
                            </span>
                          </div>
                        )}
                        {!isCollapsed && (
                          <ChevronDown className="h-3.5 w-3.5 text-[hsl(var(--muted-warm))] shrink-0" />
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top" className="w-56">
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          <Palette className="mr-2 h-4 w-4" />
                          {t('nav.theme')}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
                            <DropdownMenuRadioItem value="light" className="cursor-pointer">
                              {t('nav.light')}
                            </DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="dark" className="cursor-pointer">
                              {t('nav.dark')}
                            </DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          <Globe className="mr-2 h-4 w-4" />
                          {t('nav.language')}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
                          {languages.map((lang) => (
                            <DropdownMenuItem
                              key={lang.code}
                              onClick={() => {
                                i18n.changeLanguage(lang.code);
                                document.documentElement.dir = lang.code === 'ar' ? 'rtl' : 'ltr';
                              }}
                              className="cursor-pointer"
                            >
                              <span className="mr-2">{lang.flag}</span>
                              {lang.name}
                              {i18n.language === lang.code && (
                                <Check className="ml-auto h-4 w-4" />
                              )}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      {!isAdmin && tier !== 'pro' && (
                        <DropdownMenuItem onClick={handleUpgrade} className="cursor-pointer">
                          <ArrowUpCircle className="mr-2 h-4 w-4" />
                          {t('nav.upgradePlan')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        {t('nav.settings')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => window.dispatchEvent(new Event("open-keyboard-shortcuts"))}
                        className="cursor-pointer"
                      >
                        <Keyboard className="mr-2 h-4 w-4" />
                        Keyboard shortcuts
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('nav.signOut')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {showUpgrade && (
                    <div className="px-3 pt-2.5 pb-3 border-t border-[hsl(var(--ink)/0.06)]">
                      <div className="flex flex-col gap-1.5 mb-2.5">
                        {['Unlimited analyses', 'Citation-ready exports', 'Sample essay generator'].map((f) => (
                          <div key={f} className="flex items-center gap-1.5 text-[11.5px] text-[hsl(var(--muted-warm))] leading-tight tracking-[-0.005em]">
                            <span className="text-[13px] font-bold leading-none" style={{ color: '#7A6000' }}>+</span>
                            <span>{f}</span>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleUpgrade}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-semibold tracking-[-0.005em] border transition-all hover:-translate-y-0.5"
                        style={{
                          background: '#FBF4DC',
                          color: '#7A6000',
                          borderColor: 'rgba(227,184,77,.45)',
                        }}
                      >
                        Upgrade to Pro
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </SidebarFooter>
        )}
      </Sidebar>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dialogs.deleteAnalysis')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dialogs.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeleteId) {
                  handleDelete(pendingDeleteId);
                  setPendingDeleteId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share dialog controlled from sidebar */}
      {shareAnalysis && (
        <ShareDialog
          analysisId={shareAnalysis.id}
          isShared={shareAnalysis.is_shared ?? false}
          shareToken={shareAnalysis.share_token}
          onShareChange={handleShareChange}
          analysisData={{ title: shareAnalysis.title }}
          open={shareDialogOpen}
          onOpenChange={(open) => {
            setShareDialogOpen(open);
            if (!open) setShareAnalysis(null);
          }}
          hideTrigger
        />
      )}
    </>
  );
}
